/**
 * SessionContext - React Context for managing session state.
 *
 * This is a composite context that wraps the granular contexts:
 * - SessionDataContext: Session metadata
 * - TranscriptContext: Conversation transcript
 * - ImagesContext: Generated images
 *
 * The useSession hook provides backward compatibility by combining
 * all three contexts into a single interface. For better performance,
 * prefer using the granular hooks (useSessionData, useTranscript, useImages)
 * when components only need a subset of the data.
 *
 * Performance Note:
 * The old monolithic context caused all consumers to re-render on any
 * state change. The new split architecture allows components to subscribe
 * only to the data they need, reducing unnecessary re-renders.
 */

/* eslint-disable react-refresh/only-export-components -- Context exports hook alongside provider */

import { useCallback, useMemo, type ReactNode } from 'react'
import type { CreateSessionRequest, SendMessageRequest, SendMessageResponse } from '@/services/api'
import {
  SessionDataProvider,
  useSessionData,
  type SessionDataState,
} from './SessionDataContext'
import {
  TranscriptProvider,
  useTranscript,
  type TranscriptState,
} from './TranscriptContext'
import {
  ImagesProvider,
  useImages,
  type ImagesState,
} from './ImagesContext'

// Re-export granular contexts for direct use
export { SessionDataProvider, useSessionData } from './SessionDataContext'
export { TranscriptProvider, useTranscript } from './TranscriptContext'
export { ImagesProvider, useImages } from './ImagesContext'

// Re-export types
export type { SessionDataState, SessionDataActions } from './SessionDataContext'
export type { TranscriptState, TranscriptActions } from './TranscriptContext'
export type { ImagesState, ImagesActions } from './ImagesContext'

/**
 * Session state (backward compatible)
 */
export interface SessionState {
  session: SessionDataState['session']
  transcript: TranscriptState['transcript']
  images: ImagesState['images']
  isLoading: boolean
  error: string | null
}

/**
 * Session actions (backward compatible)
 */
export interface SessionActions {
  createSession: (request: CreateSessionRequest) => Promise<void>
  sendMessage: (request: SendMessageRequest) => Promise<SendMessageResponse>
  requestImageIteration: (feedback: string) => Promise<void>
  loadSession: (sessionId: string) => Promise<void>
  clearSession: () => void
}

/**
 * Session context value (backward compatible)
 */
export interface SessionContextValue extends SessionState, SessionActions {}

/**
 * Session provider props
 */
export interface SessionProviderProps {
  children: ReactNode
}

/**
 * Composite session provider that wraps all granular providers
 *
 * This maintains backward compatibility while enabling components
 * to use granular hooks for better performance.
 */
export function SessionProvider({ children }: SessionProviderProps) {
  return (
    <SessionDataProvider>
      <TranscriptProvider>
        <ImagesProvider>
          {children}
        </ImagesProvider>
      </TranscriptProvider>
    </SessionDataProvider>
  )
}

/**
 * Hook to use session context (backward compatible)
 *
 * Combines all three granular contexts into a single interface.
 * For better performance, prefer using:
 * - useSessionData() for session metadata only
 * - useTranscript() for conversation transcript only
 * - useImages() for generated images only
 */
export function useSession(): SessionContextValue {
  const sessionData = useSessionData()
  const transcript = useTranscript()
  const images = useImages()

  // Combine loading states - true if any is loading
  const isLoading = sessionData.isLoading || transcript.isLoading || images.isLoading

  // Combine error states - first error found
  const error = sessionData.error || transcript.error || images.error

  /**
   * Create new session (loads all data)
   *
   * Uses Promise.allSettled to handle partial failures gracefully.
   * If any parallel load fails, all contexts are reset to prevent
   * inconsistent state.
   */
  const createSession = useCallback(async (request: CreateSessionRequest) => {
    const session = await sessionData.createSession(request)

    // Load transcript and images for the new session
    // Use allSettled to capture all results even if some fail
    const results = await Promise.allSettled([
      transcript.loadTranscript(session.session_id),
      images.loadImages(session.session_id),
    ])

    // Check for failures
    const failures = results
      .map((result, index) => {
        if (result.status === 'rejected') {
          const operation = ['transcript', 'images'][index]
          console.error(`[SessionContext] Failed to load ${operation}`, result.reason)
          return { operation, error: result.reason as unknown }
        }
        return null
      })
      .filter((f): f is { operation: string; error: unknown } => f !== null)

    // If any failed, reset all contexts and throw
    if (failures.length > 0) {
      sessionData.clearSession()
      transcript.clearTranscript()
      images.clearImages()
      const failedOps = failures.map(f => f.operation).join(' and ')
      console.error('[SessionContext] Session setup failures:', failures)
      throw new Error(`Session created but failed to load ${failedOps}. Please try again.`)
    }
  }, [sessionData, transcript, images])

  /**
   * Send conversation message
   */
  const sendMessage = useCallback(async (request: SendMessageRequest): Promise<SendMessageResponse> => {
    if (!sessionData.session) {
      throw new Error('No active session')
    }

    return transcript.sendMessage(sessionData.session.session_id, request)
  }, [sessionData.session, transcript])

  /**
   * Request image iteration (refinement)
   */
  const requestImageIteration = useCallback(async (feedback: string) => {
    if (!sessionData.session) {
      throw new Error('No active session')
    }

    await images.requestImageIteration(sessionData.session.session_id, {
      refinement_feedback: feedback,
    })
  }, [sessionData.session, images])

  /**
   * Load existing session (loads all data)
   *
   * Uses Promise.allSettled to handle partial failures gracefully.
   * If any parallel load fails, all contexts are reset to prevent
   * inconsistent state.
   */
  const loadSession = useCallback(async (sessionId: string) => {
    await sessionData.loadSession(sessionId)

    // Load transcript and images for the session
    // Use allSettled to capture all results even if some fail
    const results = await Promise.allSettled([
      transcript.loadTranscript(sessionId),
      images.loadImages(sessionId),
    ])

    // Check for failures
    const failures = results
      .map((result, index) => {
        if (result.status === 'rejected') {
          const operation = ['transcript', 'images'][index]
          console.error(`[SessionContext] Failed to load ${operation}`, result.reason)
          return { operation, error: result.reason as unknown }
        }
        return null
      })
      .filter((f): f is { operation: string; error: unknown } => f !== null)

    // If any failed, reset all contexts and throw
    if (failures.length > 0) {
      sessionData.clearSession()
      transcript.clearTranscript()
      images.clearImages()
      const failedOps = failures.map(f => f.operation).join(' and ')
      console.error('[SessionContext] Load session failures:', failures)
      throw new Error(`Failed to load ${failedOps}. Please try again.`)
    }
  }, [sessionData, transcript, images])

  /**
   * Clear all session state
   */
  const clearSession = useCallback(() => {
    sessionData.clearSession()
    transcript.clearTranscript()
    images.clearImages()
  }, [sessionData, transcript, images])

  return useMemo(() => ({
    // State
    session: sessionData.session,
    transcript: transcript.transcript,
    images: images.images,
    isLoading,
    error,
    // Actions
    createSession,
    sendMessage,
    requestImageIteration,
    loadSession,
    clearSession,
  }), [
    sessionData.session,
    transcript.transcript,
    images.images,
    isLoading,
    error,
    createSession,
    sendMessage,
    requestImageIteration,
    loadSession,
    clearSession,
  ])
}
